import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { UserRole } from "@/lib/api/types";
import authApi, { AUTH_SESSION_UPDATED_EVENT } from "@/lib/api/auth";
import { tenantSettingsKey } from "@/hooks/use-tenant-settings";
import { getPublicLoginRoute } from "@/lib/product-profile-session";

// IMPORTANT: Must match keys in lib/api/auth.ts
const USER_KEY = "eca_user";

interface User {
  email: string;
  full_name: string;
  role: UserRole;
  permissions?: string[];
  capabilities?: string[];
  ai_guidance_enabled?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpiresAt: number | null;
  sessionMinutesRemaining: number | null;
  isSessionExpiring: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function loadCachedUser() {
  try {
    const storedUser = localStorage.getItem(USER_KEY);
    return storedUser ? JSON.parse(storedUser) as User : null;
  } catch {
    localStorage.removeItem(USER_KEY);
    return null;
  }
}

function readSessionExpiresAt(): number | null {
  if (typeof authApi.getSessionExpiresAt !== "function") {
    return null;
  }
  return authApi.getSessionExpiresAt();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    // Initialize from localStorage
    return authApi.hasSessionHint() ? loadCachedUser() : null;
  });
  const [isLoading, setIsLoading] = useState<boolean>(() => authApi.hasSessionHint());
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(() =>
    authApi.hasSessionHint() ? readSessionExpiresAt() : null
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const clearAuthState = useCallback(() => {
    authApi.clearAuth();
    setUser(null);
    setIsLoading(false);
    setSessionExpiresAt(null);
    queryClient.removeQueries({ queryKey: tenantSettingsKey });
  }, [queryClient]);

  useEffect(() => {
    const syncSession = () => {
      setSessionExpiresAt(readSessionExpiresAt());
    };

    syncSession();
    window.addEventListener(AUTH_SESSION_UPDATED_EVENT, syncSession);
    const intervalId = window.setInterval(syncSession, 30_000);

    return () => {
      window.removeEventListener(AUTH_SESSION_UPDATED_EVENT, syncSession);
      window.clearInterval(intervalId);
    };
  }, []);

  // Rehydrate user from localStorage on mount (in case token exists but user wasn't loaded)
  useEffect(() => {
    let isActive = true;

    if (!authApi.hasSessionHint()) {
      clearAuthState();
      return () => {
        isActive = false;
      };
    }

    const hydrateUser = async () => {
      try {
        const hydratedUser = await authApi.getCurrentUser();
        if (isActive) {
          setUser(hydratedUser);
        }
      } catch {
        if (isActive) {
          clearAuthState();
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void hydrateUser();
    return () => {
      isActive = false;
    };
  }, [clearAuthState]);

  useEffect(() => {
    let isActive = true;
    if (isLoading || !user) return () => {
      isActive = false;
    };
    if (user.ai_guidance_enabled !== undefined) {
      return () => {
        isActive = false;
      };
    }

    const hydrateUser = async () => {
      try {
        const hydratedUser = await authApi.getCurrentUser();
        if (isActive) {
          setUser(hydratedUser);
        }
      } catch {
        // authApi handles storage cleanup + auth:logout event on 401s
      }
    };

    void hydrateUser();
    return () => {
      isActive = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-hydrate when ai_guidance_enabled changes, not on every user object mutation
  }, [isLoading, user?.ai_guidance_enabled]);

  useEffect(() => {
    if (user?.ai_guidance_enabled === undefined) return;
    queryClient.setQueryData(tenantSettingsKey, (previous: Record<string, unknown> | undefined) => ({
      ...(previous || {}),
      ai_guidance_enabled: user.ai_guidance_enabled,
    }));
  }, [queryClient, user?.ai_guidance_enabled]);

  // Listen for auth:logout events from API layer (e.g., 401 responses)
  useEffect(() => {
    const handleLogout = () => {
      clearAuthState();
      navigate(getPublicLoginRoute(window.location.search));
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [clearAuthState, navigate]);

  const login = (newUser: User) => {
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setUser(newUser);
    setIsLoading(false);
    setSessionExpiresAt(readSessionExpiresAt());
    if (newUser.ai_guidance_enabled !== undefined) {
      queryClient.setQueryData(tenantSettingsKey, (previous: Record<string, unknown> | undefined) => ({
        ...(previous || {}),
        ai_guidance_enabled: newUser.ai_guidance_enabled,
      }));
    }
    // Note: Navigation is handled by LoginPage to support redirect-after-login
  };

  const logout = () => {
    void authApi.logout().finally(() => {
      clearAuthState();
      navigate(getPublicLoginRoute(window.location.search));
    });
  };

  const sessionRemainingMs = useMemo(
    () => (sessionExpiresAt ? Math.max(sessionExpiresAt - Date.now(), 0) : null),
    [sessionExpiresAt]
  );
  const sessionMinutesRemaining = sessionRemainingMs == null
    ? null
    : Math.max(1, Math.ceil(sessionRemainingMs / 60_000));
  const isSessionExpiring =
    sessionRemainingMs != null && sessionRemainingMs > 0 && sessionRemainingMs <= 15 * 60_000;

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: !!user,
        isLoading,
        sessionExpiresAt,
        sessionMinutesRemaining,
        isSessionExpiring,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
